import { ReportCommonApi } from 'app/area/reports/types-api';
import * as React from 'react';
import { ReactNode, useContext, useMemo, useRef } from 'react';
import { v4 } from 'uuid';

import { useInstanceSlice as useCaseListingInstanceSlice } from 'app/area/case-report/case-listing/hooks';
import { useInstanceSlice as useFavouriteInstanceSlice } from 'app/area/favourite/hooks';
import { useInstanceSlice as useFilterInstanceSlice } from 'app/area/filters/hooks';
import { useInstanceSlice as useReportInstanceSlice } from 'app/area/reports/hooks';
import { useInstanceSlice as useRiskReportInstanceSlice } from 'app/area/case-report/risk-listing/hooks';

interface FavouriteContextData {
  instanceKey: string;
  autoExecute: React.MutableRefObject<boolean>;
  options: FavouriteInstanceProps;
}

interface FavouriteInstanceProps {
  hideHeader?: boolean;
  hideFilters?: boolean;
  hideVisualizationOptions?: boolean;
  executionCounter?: number;
  layoutTypeContextId?: number;
  reportApiOverrides?: Partial<ReportCommonApi>;
  height?: number;
  hideCaseListingHeader?: boolean;
}

export const FavouriteContext = React.createContext(undefined as FavouriteContextData);

// Creates a unique instanceKey and provides it in context
export function FavouriteInstance({
  children, hideHeader, hideFilters, hideVisualizationOptions,
  executionCounter, layoutTypeContextId, reportApiOverrides, height, hideCaseListingHeader
}: FavouriteInstanceProps & { children: ReactNode }) {

  const instanceKey = useRef(v4());
  const autoExecute = useRef<boolean>(true);
  const contextValue = useMemo(() => ({
    instanceKey: instanceKey.current,
    autoExecute,
    options: {
      hideHeader,
      hideFilters,
      hideVisualizationOptions,
      executionCounter,
      layoutTypeContextId,
      reportApiOverrides,
      height,
      hideCaseListingHeader
    }
  }), [
    instanceKey.current,
    autoExecute.current,
    hideHeader,
    hideFilters,
    hideVisualizationOptions,
    executionCounter,
    layoutTypeContextId,
    reportApiOverrides,
    height,
    hideCaseListingHeader
  ]);

  return (
    <FavouriteContext.Provider value={contextValue}>
      <FavouriteInstanceHandler>
        {children}
      </FavouriteInstanceHandler>
    </FavouriteContext.Provider>
  );
}

// Handles the lifecycle of slices in all the different subareas for a favourite
function FavouriteInstanceHandler({ children }: { children: ReactNode }) {
  useCaseListingInstanceSlice();
  useFavouriteInstanceSlice();
  useFilterInstanceSlice();
  useReportInstanceSlice();
  useRiskReportInstanceSlice();

  return <>{children}</>;
}

export function useInstanceKey() {
  return useContext(FavouriteContext)?.instanceKey;
}

export function useInstanceOptions(): FavouriteInstanceProps {
  return useContext(FavouriteContext)?.options ?? {};
}

export function useAutoExecuteContext() {
  return useContext(FavouriteContext)?.autoExecute;
}
